import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Divider,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Input,
  Spacer,
  Text,
  useEditableControls,
} from '@chakra-ui/react';
import { IconPencilLine } from 'central-icons/IconPencilLine';
import { startCase } from 'lodash';
import { useRef } from 'react';

import { FeatureFlag } from '@revelio/core';
import { IconButton } from '@revelio/ui';

import { useHideNav } from '../../../shared/hooks/useHideNav';
import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import { useUserPreferences } from '../../../shared/user-preferences/hooks/useUserPreferences';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../shared/user-preferences/types';
import {
  GlobalTopRightContent,
  PoweredByRevelioLabsLogo,
} from '../../GlobalTopRightContent';
import { FilterId } from '../filters/types/filters';
import {
  type AnalyticsEntity,
  getAnalyticsEntityFromRevelioPageId,
} from '../utils/revelio-primary-filter-entity-switch';
import {
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../utils/routing-helpers';
import { AnalyticsViewTabs } from './AnalyticsViewTabs';
import { useEditMode } from './EditModeProvider/EditModeProvider';
import styles from './Header.module.css';
import { PageActionBar } from './PageActionBar/PageActionBar';
import {
  usePageConfig,
  useTabContentConfig,
} from './PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from './PageConfigProvider/types';

export const Header = ({
  isReportPage = false,
}: {
  isReportPage?: boolean;
}) => {
  const { config: fullConfig, dispatch: fullConfigDispatch } = usePageConfig();
  const { name, revelioPageId } = fullConfig;

  const { config: tabContent } = useTabContentConfig();

  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);
  const isThemedAppearance =
    isNewDashboardLayout &&
    (preferences[UserPreferenceKey.ColorMode] ?? 'legacy') !== 'legacy';
  const hideNav = useHideNav();
  const analyticsEntity = getAnalyticsEntityFromRevelioPageId(revelioPageId);

  const primaryFilterId =
    tabContent.primaryFilter?.id ??
    getPrimaryFilterIdForScreenerV2Page(revelioPageId);

  // Show the tab bar on custom-dash pages too (not just analytics views) so
  // users can hop between any tab without leaving the header. Discovery /
  // screener / report pages still opt out.
  const customPageEntity: AnalyticsEntity | undefined = (() => {
    if (analyticsEntity || revelioPageId || isReportPage) return undefined;
    if (primaryFilterId === FilterId.COMPANY) return 'company';
    if (primaryFilterId === FilterId.GEOGRAPHY) return 'geography';
    if (primaryFilterId === FilterId.ROLE) return 'role';
    return undefined;
  })();
  const tabBarEntity = analyticsEntity ?? customPageEntity;

  const primaryFilterLabel = (() => {
    if (primaryFilterId) return startCase(primaryFilterId.toLowerCase());
    if (revelioPageId === RevelioPageId.TALENT_DISCOVERY) return 'Individual';
    if (revelioPageId === RevelioPageId.POSTING_DISCOVERY) return 'Postings';
    return null;
  })();

  const isDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
    revelioPageId === RevelioPageId.POSTING_DISCOVERY ||
    isScreenerV2Page(revelioPageId);

  const { editMode } = useEditMode();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (value: string) => {
    const finalTitle = value.trim() || 'Untitled';

    if (finalTitle !== name) {
      fullConfigDispatch({ type: 'UPDATE_PAGE_TITLE', payload: finalTitle });
    }
  };

  if (isNewDashboardLayout) {
    const titleNode = (() => {
      if (analyticsEntity) {
        return (
          <Text className={styles.pageTitle}>
            {`${primaryFilterLabel ?? ''} Analytics`.trim()}
          </Text>
        );
      }
      if (isDiscoveryPage && primaryFilterLabel) {
        return (
          <Text className={styles.pageTitle}>
            {`${primaryFilterLabel} Discovery`}
          </Text>
        );
      }
      return (
        <Flex alignItems="center" minWidth={0}>
          {primaryFilterLabel && (
            <>
              <Text
                className={styles.pageTitle}
                marginRight="3px"
                flexShrink={0}
              >
                {`${primaryFilterLabel ?? ''} Analytics`.trim()}
              </Text>
              <ChevronRightIcon
                boxSize="5"
                color="text.primary"
                transform="translateY(1px)"
                flexShrink={0}
              />
            </>
          )}
          {editMode ? (
            <Editable
              key={name}
              defaultValue={name}
              onSubmit={handleSubmit}
              submitOnBlur
              display="flex"
              alignItems="center"
              minWidth={0}
            >
              <EditablePreview
                className={`${styles.pageTitle} ${styles.editablePreview}`}
                cursor="pointer"
                py="2px"
                overflow="hidden"
                textOverflow="ellipsis"
                minWidth={0}
                _hover={{ bg: 'gray.100' }}
              />
              <Input
                ref={inputRef}
                as={EditableInput}
                className={styles.titleInput}
                variant="flushed"
                minWidth={0}
              />
              <EditButton />
            </Editable>
          ) : (
            <Text
              className={styles.pageTitle}
              overflow="hidden"
              textOverflow="ellipsis"
              minWidth={0}
            >
              {name}
            </Text>
          )}
        </Flex>
      );
    })();

    return (
      <>
        <Flex
          role="group"
          className={styles.headerRow}
          alignItems="flex-start"
          minHeight="32px"
          justifyContent="space-between"
          gap={6}
          data-testid="page-header-label"
        >
          {titleNode}
          <Spacer />
          {tabBarEntity && !hideNav && (
            <>
              <AnalyticsViewTabs entity={tabBarEntity} />
              <Divider
                className={styles.divider}
                orientation="vertical"
                borderColor="var(--hdr-divider)"
                height="20px"
                alignSelf="center"
                marginLeft={-2}
                marginRight={-3}
              />
            </>
          )}
          <GlobalTopRightContent showPoweredBy={!tabBarEntity}>
            <PageActionBar isReportPage={isReportPage} />
          </GlobalTopRightContent>
        </Flex>
        {!isThemedAppearance && (
          <Divider
            className={styles.divider}
            borderColor="var(--hdr-divider)"
          />
        )}
        {tabBarEntity && (
          <Flex justifyContent="flex-end">
            <PoweredByRevelioLabsLogo />
          </Flex>
        )}
      </>
    );
  }

  return (
    <>
      <Flex
        role="group"
        alignItems="center"
        marginBottom={2}
        minHeight="32px"
        justifyContent="space-between"
        data-testid="page-header-label"
      >
        <Flex alignItems="center" minWidth={0}>
          {primaryFilterLabel && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="600"
                lineHeight="32px"
                marginRight="3px"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                {primaryFilterLabel}
              </Text>

              <ChevronRightIcon
                boxSize="5"
                color="text.primary"
                transform="translateY(1px)"
                flexShrink={0}
              />
            </>
          )}
          {editMode ? (
            <Editable
              key={name}
              defaultValue={name}
              onSubmit={handleSubmit}
              submitOnBlur
              display="flex"
              alignItems="center"
              minWidth={0}
            >
              <EditablePreview
                fontSize="1.25rem"
                fontWeight="600"
                lineHeight="28px"
                cursor="pointer"
                py="2px"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                minWidth={0}
                _hover={{
                  bg: 'gray.100',
                }}
              />
              <Input
                ref={inputRef}
                as={EditableInput}
                height="32px"
                lineHeight="32px"
                fontSize="1.25rem"
                variant="flushed"
                minWidth={0}
              />
              <EditButton />
            </Editable>
          ) : (
            <Text
              fontSize="1.25rem"
              fontWeight="600"
              lineHeight="32px"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              minWidth={0}
            >
              {name}
            </Text>
          )}
        </Flex>
        <Spacer />
        <GlobalTopRightContent>
          <PageActionBar isReportPage={isReportPage} />
        </GlobalTopRightContent>
      </Flex>
      <Divider {...(isReportPage && { marginBottom: '12px' })} />
    </>
  );
};

const EditButton = () => {
  const { isEditing, getEditButtonProps } = useEditableControls();

  if (isEditing) return null;

  return (
    <IconButton
      size="sm"
      icon={<IconPencilLine size={16} />}
      aria-label="Edit page name"
      variant="ghost"
      className={styles.editButton}
      {...getEditButtonProps()}
    />
  );
};
